<template>
  <div>
    <target-booking-dialog
      :member-name="targetMember.member ? targetMember.member.name :''"
      :memberships="multipleMemberships"
      :is-target-booking-dialog-visible.sync="targetBookingDialogVisible"
      @close="closeTargetBookingDialog"
      @complete="handleTargetBooking"
    />
    <el-dialog
      title="반복 개설된 수업입니다."
      custom-class="fifl-message-box"
      top="0"
      :visible.sync="confirmDialogVisible"
      :append-to-body="true"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <p>지정 예약 리스트를 모든 수업에 적용하시겠습니까, 아니면 현재 선택된 수업에만 적용하시겠습니까?</p>
      <div
        slot="footer"
        v-loading="isDialogLoading"
        class="dialog-footer"
      >
        <el-button
          class="white"
          style="float:left;"
          @click="closeConfirmDialog"
        >
          취소
        </el-button>
        <el-button
          class="white"
          @click="bookTargetMember({isBulk: false})"
        >
          이 수업만 적용
        </el-button>
        <el-button
          class="gray"
          @click="bookTargetMember({isBulk: true})"
        >
          반복 적용
        </el-button>
      </div>
    </el-dialog>

    <div 
      v-show="!bookings.length && isBookedMemberLoading"
      class="booked-member-loading__container"
    >
      <el-spinner 
        :stroke-width="1"
      />
    </div>
    <el-form
      class="booked-member-list-container"
      label-position="top"
      label-width="100px"
    >
      <div 
        v-show="bookings.length && isBookedMemberLoading"
        class="booked-member-loading__container"
      >
        <el-spinner 
          :stroke-width="1"
        />
      </div>
      <div
        v-show="bookings.length > 0"
        class="booking-guide"
      >
        오른쪽 <span class="icon sm ic-more" /> 을 눌러 예약 및 출결 상태를 변경할 수 있습니다.
      </div>

      <el-row :gutter="15">
        <el-col :span="24">
          <el-form-item label="예약 회원">
            <target-member-search-box
              placeholder="지정 예약할 회원을 선택해주세요."
              :options="vaildMembers"
              @select="onSelectTargetMember"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row> 
        <el-col
          :span="24"
          class="booked-member-list__wrapper"
        >
          <booked-member
            v-for="booking in bookings"
            :key="booking.id"
            :data="booking"
            :child-course="childCourse"
            @update="onBookedMemberUpdate"
          />
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import BookedMember from '@/components/BookedMember';
import { mapGetters } from 'vuex';
import TargetBookingDialog from '@/components/TargetBookingDialog';
import { computedGetters } from '@/utils/vuex-helper';
import { BOOKING_FAIL_MESSAGES } from '@/constants';
import TargetMemberSearchBox from '@/components/TargetMemberSearchBox';
import { fiflboxApi } from '@/utils/api';
import { numberWithCommas } from '@/utils/etc';

const INVALID_MEMBERSHIP =
  '님은 수강 가능한 회원권이 없거나 횟수가 부족합니다. 회원 상세 페이지로 이동하여 회원권을 먼저 재등록해주세요.';
const MULTIPLE_MODE = 'MULTIPLE_MODE';
export default {
  name: 'BookingForm',
  components: { TargetMemberSearchBox, BookedMember, TargetBookingDialog },
  props: ['childCourse'],
  data() {
    return {
      targetBookingDialogVisible: false,
      confirmDialogVisible: false,
      targetMembershipId: null,
      isDialogLoading: false,
      isBookedMemberLoading: false,
      bookings: [],
      targetMember: {},
      multipleMemberships: []
    };
  },
  watch: {
    childCourse: {
      immediate: true,
      handler(value) {
        if (!value.id) return;
        this.bookings = [];
        this.syncBookingData();
      }
    }
  },
  computed: {
    ...mapGetters(['getParentCourseByIds']),
    ...computedGetters([
      'membershipSystem',
      'vaildMembers',
      'publicParentCourses'
    ])
  },
  methods: {
    isMembershipAvailableForCourse(childMembership) {
      if (childMembership.balance === -1) {
        return true;
      }

      const leftPoint = childMembership.balance - childMembership.consumed;

      return leftPoint > 0;
    },
    async getAvailableMembership(member) {
      const memberships = await this.syncTargetMemberMemberships(member);
      // case 1 : 유효회원권 아예없는 경우
      if (memberships.length === 0) {
        return null;
      }
      // case 2 : 유효회원권이 1개 있는 경우
      else if (memberships.length === 1) {
        return this.isMembershipAvailableForCourse(memberships[0])
          ? memberships[0]
          : null;
      }
      // case 3 : 유효회원권이 여러개 있는 경우(중복회원권)
      else if (memberships.length > 1) {
        this.targetBookingDialogVisible = true;
        this.multipleMemberships = memberships;
        return MULTIPLE_MODE;
      }
    },
    presentLeftPoint(childMembership) {
      if (childMembership.balance === -1) {
        return `잔여 ${this.membershipSystem.long}: 무제한`;
      }
      let leftPoint =
        childMembership.balance -
        childMembership.consumed -
        childMembership.wait_consumed;
      return `잔여 ${this.membershipSystem.middle}: ${numberWithCommas(
        leftPoint
      )}${this.membershipSystem.short}`;
    },
    closeConfirmDialog() {
      this.confirmDialogVisible = false;
    },
    async bookTargetMember({ isBulk }) {
      this.isDialogLoading = true;
      let createdBooking = null;
      try {
        createdBooking = await fiflboxApi.v1.postBookings({
          status: 'BOOKED',
          dt: this.childCourse.dt,
          by_admin: true,
          consumed: this.childCourse.price,
          child_membership: this.targetMembershipId,
          child_course: this.childCourse.id,
          bulk: isBulk
        });

        // 동시예약 체크 로직 동작
        // bulk false에서만 일단 동작하도록
        if (!isBulk) {
          await fiflboxApi.v1.getCheckBookingInBookedList({
            booking: createdBooking.data.id
          });
        }
      } catch (err) {
        this.showFailMessage(err.response.data);
        if (err.response.data.error_code === '121') {
          await fiflboxApi.v1.putBookings(createdBooking.data.id, {
            status: 'CANCELLED',
            dt: this.childCourse.dt,
            by_admin: true,
            consumed: this.childCourse.price,
            child_membership: this.targetMembershipId,
            child_course: this.childCourse.id,
            bulk: false
          });
        }
      } finally {
        await this.syncBookingData();
        this.$emit('update', {});
        this.isDialogLoading = false;
        this.confirmDialogVisible = false;
      }
    },
    async showFailMessage(errorData) {
      if (errorData.error_code === '109') {
        errorData.error_code = this.membershipSystem.value;
      } else if (errorData.error_code === '802') {
        await this.syncBookingData();
        this.$emit('update', {});
        return;
      } else if (errorData.error_code === '121') {
        return await this.$alert(errorData.error_detail, '지정 예약 불가');
      }

      let errorMessage = BOOKING_FAIL_MESSAGES[errorData.error_code];

      if (errorMessage.type === 'member-confirm') {
        return this.confirmMemberIneligible(errorMessage.message);
      }

      await this.$alert(errorMessage.message, '지정 예약 불가');
    },
    async confirmMemberIneligible(errorMessage) {
      try {
        await this.$confirm(
          `${this.targetMember.member.name}${errorMessage}`,
          '지정 예약 불가',
          {
            cancelButtonText: '회원 상세 페이지로 이동'
          }
        );
      } catch (err) {
        return this.$router
          .push('/member/' + this.targetMember.id)
          .catch(() => {});
      }
    },
    async syncTargetMemberMemberships(member) {
      const targetDate = new Date(this.childCourse.dt).toDateFormat();

      let { data } = await fiflboxApi.v1.getChildMemberships({
        enrollment: member.id,
        start_before: targetDate,
        end_after: targetDate
      });
      return data;
    },
    async confirmMembershipForBooking(membership) {
      try {
        await this.$confirm(
          `${
            this.targetMember.member.name
          }님을 본 수업에 지정 예약하시겠습니까?<br>[${
            membership.parent_membership.name
          }, ${this.presentLeftPoint(membership)}]에서 ${
            this.membershipSystem.long
          } 차감될 예정입니다.`,
          '지정 예약',
          {
            dangerouslyUseHTMLString: true
          }
        );

        return true;
      } catch (err) {
        return false;
      }
    },
    async onSelectTargetMember(enrollment, isMultipleMembershipMode) {
      //해당 함수는 1.지정예약 회원 선택 시 와 2.중복회원권 선택 시에 모두 쓰이는 함수입니다.
      //회원권이 없거나, 1개만 있을경우엔 중복회원권 창을 띄우지 않도록 합니다.
      //isMultipleMemebershipMode 변수로 중복회원권모드인지 여부를 판단합니다.
      if (!isMultipleMembershipMode) {
        this.targetMember = enrollment;
      }

      const isBookedMember = this.bookings.find(
        booking => booking.member.id === this.targetMember.member.id
      );

      if (isBookedMember) {
        this.$message('이미 예약된 회원입니다');
        return false;
      }
      let membership;
      if (!isMultipleMembershipMode) {
        membership = await this.getAvailableMembership(enrollment);
        if (membership === null) {
          return this.confirmMemberIneligible(INVALID_MEMBERSHIP);
        } else if (membership === MULTIPLE_MODE) {
          return false;
        }
      } else {
        membership = enrollment;
      }
      //여기에서 enrollment는 중복회원권 모드에서 선택된 회원권입니다.
      const isConfirmed = await this.confirmMembershipForBooking(membership);

      if (!isConfirmed) {
        return false;
      }

      this.targetMembershipId = membership.id;
      if (this.childCourse.recurrence !== null) {
        this.confirmDialogVisible = true;
      } else {
        this.bookTargetMember({
          isBulk: false
        });
      }
      return true;
    },
    async syncBookingData() {
      this.isBookedMemberLoading = true;
      let dt = new Date(this.childCourse.dt).toJSON();
      let { data: bookings } = await fiflboxApi.v1.getBookings({
        child_course: this.childCourse.id,
        dt: dt,
        status: ['BOOKED', 'PASS']
      });

      this.bookings = bookings;

      this.$emit('sync', this.bookings);
      this.isBookedMemberLoading = false;
    },
    onBookedMemberUpdate() {
      this.syncBookingData();
      this.$emit('update', {});
    },
    closeTargetBookingDialog() {
      this.targetBookingDialogVisible = false;
    },
    async handleTargetBooking(targetMemberShip) {
      if (targetMemberShip) {
        const isComplete = await this.onSelectTargetMember(
          targetMemberShip,
          true
        );
        if (isComplete) {
          this.targetBookingDialogVisible = false;
        }
      } else {
        this.targetBookingDialogVisible = false;
      }
    }
  }
};
</script>

<style lang="scss">
.booking-guide {
  position: absolute;
  top: 16px;
  right: 30px;
  height: 24px;
  line-height: 24px;

  .icon.sm.ic-more {
    width: 10px;
    height: 22px;
    border-radius: 6px;
    border: 1px solid #b9b9b9;
    background-color: #f8f8f8;
    vertical-align: top;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
}
.booked-member-list-container {
  position: relative;
}

.booked-member-loading__container {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  backdrop-filter: blur(1px);
  display: flex;
  z-index: 10;
  .el-spinner {
    margin: auto;
  }
}

.booked-member-list__wrapper {
  @media screen and (max-width: 768px) {
    padding-bottom: 60px;
  }
}
</style>
